import { endSession } from './session';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001';

// Single in-flight refresh — a burst of parallel 401s only fires one /auth/refresh.
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    if (typeof window === 'undefined') return null;
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return null;
    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as {
        access_token: string;
        refresh_token: string;
      };
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      return data.access_token;
    } catch {
      return null;
    }
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

// Session teardown lives in ./session so all four exit paths clear the SAME
// keys. This one used to miss `user_role`, and it is the path that fires most
// often (silent expiry), so it stranded a stale role on the machine every time.

/**
 * Authenticated fetch with transparent refresh: attaches the access token,
 * and on a 401 refreshes it once and retries before giving up (wipe+login).
 * Returns the raw Response — no ok-check, no body parsing, and no forced
 * Content-Type (so FormData uploads and blob/stream downloads work).
 * Every client-side request that sends a Bearer token should go through
 * this (or apiFetch); a raw fetch() 401s hard when the 15-min token expires.
 */
export async function apiFetchRaw(
  path: string,
  options?: RequestInit,
): Promise<Response> {
  // Tolerate already-prefixed URLs from builders like assetFileUrl()
  // (which bake in NEXT_PUBLIC_API_URL — or their own "/api" default — for
  // use in <img src>). No backend route starts with /api, so this is safe.
  if (API_URL && path.startsWith(`${API_URL}/`)) path = path.slice(API_URL.length);
  else if (path.startsWith("/api/")) path = path.slice(4);
  const doFetch = (token: string | null) =>
    fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options?.headers,
      },
    });

  const initialToken =
    typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
  let res = await doFetch(initialToken);

  // A 401 from /auth/login means WRONG PASSWORD, not "your session expired".
  // Sending it down the expiry path below was silently destructive: the
  // refresh fails (there is no session yet), endSession() clears localStorage
  // and hard-navigates to /login with window.location.assign — which tore down
  // the page before the caller's error toast could render. The visible symptom
  // was a mistyped password reloading the login screen with NO message at all.
  // Found while driving the password-reset flow to its last step, which is
  // exactly this form.
  const isLoginAttempt = path.startsWith('/auth/login');

  if (res.status === 401 && !isLoginAttempt && typeof window !== 'undefined') {
    const fresh = await refreshAccessToken();
    if (fresh) {
      res = await doFetch(fresh);
    } else {
      // revoke:false — the refresh we just tried failed, so the token is
      // already rejected or the backend is unreachable. Calling /auth/logout
      // here would add an abort-timeout stall to a redirect the user is
      // already waiting on, in the one case where the network is the problem.
      await endSession({ revoke: false });
      throw new Error('Session expired');
    }
  }
  return res;
}

// Substring of the 403 detail `require_client_org` returns for a client user
// whose account has no ClientOrganization. Detected here — the one helper every
// /portal/* lib call funnels through — so the portal can show a single clear
// full-page notice instead of a generic "Failed to load…" toast on every page.
export const CLIENT_ORG_MISSING_DETAIL = 'not associated with a client organization';

// Fired (browser only) the first time a client-org-missing 403 is seen. The
// client layout listens for it and renders <PortalOrgNotice/>. Any other error
// keeps its existing per-caller behaviour.
export const PORTAL_NO_ORG_EVENT = 'portal:no-org';

/** An HTTP error from apiFetch: `message` is the response body (unchanged, so
 * existing errorDetail parsing keeps working) and `status` the HTTP code. */
export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const res = await apiFetchRaw(path, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options?.headers },
  });
  if (!res.ok) {
    const text = await res.text();
    if (
      res.status === 403 &&
      typeof window !== 'undefined' &&
      text.includes(CLIENT_ORG_MISSING_DETAIL)
    ) {
      window.dispatchEvent(new CustomEvent(PORTAL_NO_ORG_EVENT));
    }
    throw new ApiError(text, res.status);
  }
  return res.json();
}

/**
 * The human-readable message behind an apiFetch failure.
 *
 * apiFetch throws the RAW response body, which for a FastAPI error is
 * `{"detail": "..."}` — dumping that straight into a toast shows the user JSON
 * braces and quotes. This pulls the detail out (string detail, or the joined
 * `msg`es of a 422 validation list) and falls back to `fallback` whenever the
 * body is JSON we can't read a message from, so a raw body is NEVER surfaced.
 * A non-JSON body (plain text, or a client-side Error like "Session expired")
 * is passed through as-is.
 */
export function errorDetail(err: unknown, fallback: string, maxLength = 200): string {
  const raw = (err instanceof Error ? err.message : typeof err === 'string' ? err : '').trim();
  // When only the generic fallback can be shown, still say what kind of failure
  // it was, so a user report ("error 502") is diagnosable.
  if (err instanceof ApiError) fallback = `${fallback} (error ${err.status})`;
  // fetch() rejects with a TypeError whose message is browser-specific; only
  // those messages are network failures (any other TypeError is a code bug).
  if (err instanceof TypeError) {
    // fetch() network failures (browser and Node wording); any other TypeError
    // is a code bug whose message means nothing to a user.
    return /failed to fetch|fetch failed|networkerror|load failed|network request failed/i.test(raw)
      ? `${fallback} (network error)`
      : fallback;
  }
  if (!raw) return fallback;
  const cap = (s: string) => (s.length > maxLength ? `${s.slice(0, maxLength - 1)}…` : s);
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Not JSON. Anything that merely LOOKS like a body — an unparseable
    // object/array, or the HTML error page a proxy serves on a 502/504 — is
    // still hidden behind the fallback; only plain text is passed through.
    return /^[{[<]/.test(raw) ? fallback : cap(raw);
  }
  const detail = (parsed as { detail?: unknown } | null)?.detail;
  if (typeof detail === 'string' && detail.trim()) return cap(detail.trim());
  if (Array.isArray(detail)) {
    // FastAPI 422: [{loc, msg, type}, ...]
    const msgs = detail
      .map((d) => (typeof (d as { msg?: unknown })?.msg === 'string' ? (d as { msg: string }).msg : null))
      .filter((m): m is string => !!m && !!m.trim());
    if (msgs.length) return cap(msgs.join('; '));
  }
  return fallback;
}
